/**
 * What the creature nameplates tell the game report (R-0089): the pool's counts after each frame, how many texts and
 * layouts they have set so far, and, on request only, every plate held, as a browser scenario reads them.
 */

/**
 * One creature's plate as the report lists it: the creature's handle, the name and the level line it draws (empty in
 * the mark-alone form), whether it is drawn, its attitude mark's atlas frame, the name's and the level's tints, where
 * its parts stand in logical pixels at the UI scale it was laid out at, its width in logical pixels, whether it stands
 * lifted over a health bar, its rank frame's width (a hook no creature draws yet), and whether its objects sit in the
 * UI layer.
 */
export interface CreaturePlateReading {
  readonly handle: number;
  readonly name: string;
  readonly level: string;
  readonly shown: boolean;
  readonly mark: string;
  readonly nameTint: number;
  readonly levelTint: number;
  readonly markX: number;
  readonly markY: number;
  readonly nameX: number;
  readonly nameY: number;
  readonly levelX: number;
  readonly scale: number;
  readonly width: number;
  readonly lifted: boolean;
  readonly rankFrame: number;
  readonly inUiLayer: boolean;
}

/**
 * The surface's report: `active`, `free`, `created` and `shown` are the pool's plates held, waiting, made so far and
 * drawn after the last frame; `texts` and `layouts` count every text set on a plate and every time one is laid out,
 * which grow only when a creature's look, level or form, its anchor or the UI scale changes; `list` reads every plate
 * held.
 */
export interface CreaturePlateReport {
  active: number;
  free: number;
  created: number;
  shown: number;
  texts: number;
  layouts: number;
  list: () => readonly CreaturePlateReading[];
}

/**
 * A report before the surface mounts.
 *
 * @returns The report, its list empty.
 */
export function createCreaturePlateReport(): CreaturePlateReport {
  return { active: 0, free: 0, created: 0, shown: 0, texts: 0, layouts: 0, list: () => [] };
}
